<script>
  /**Dependencies*/
  import { afterUpdate } from "svelte";
  import { getRecaptcha } from "$lib/utils/getRecaptcha.js";
  import { customer } from "$lib/state/customer";
  import { validatePassword } from "$lib/utils/validatePassword";
  import { setAllItemsInToCart } from "$lib/utils/getOffer.js";
  import { fetchCustomerSignin } from "$lib/utils/fetchCustomerSignin";
  import { states } from "$lib/utils/arrayOfStates";
  import { dispatchCustomEvent, LOGIN, SIGN_UP } from "$lib/utils/events";

  /**Components*/
  import CustomerButtonAvatar from "../CustomerButtonAvatar.svelte";
  import ErrorMessage from "../ErrorMessage.svelte";
  import NaButton from "$lib/components/global/NaButton.svelte";
  import NaModal from "$lib/components/global/NA_Modal.svelte";
  import { goto } from "$app/navigation";

  /** @typedef {import('$lib/types').MagentoCustomer} MagentoCustomer */
  // customer state management
  /** @type {HTMLDivElement} */
  let headerCustomerModuleElement;

  let openedSignInSignupButtons = false;
  /**@type {MagentoCustomer |??}*/
  let account;
  customer.subscribe((value) => (account = value));

  /** @type {NaModal} */
  let signinModal;
  /** @type {NaModal} */
  let signupModal;
  /** @type {NaModal} */
  let passwordResetModal;

  let showPassword = false;

  /**@type {string}*/
  let password;
  /**@type {string}*/
  let passwordConfirm;
  /**@type {?import ('$lib/types/errorMessages').errorMessage}*/
  let passwordError;
  /** @type {?string} */
  let errorMessage = null;

  // modal controllers
  function showSignin() {
    signinModal?.showModal();
  }
  function closeSignin() {
    signinModal?.close();
  }
  function showSignup() {
    if (signinModal.isOpen) {
      closeSignin();
    }
    signupModal.showModal();
  }
  function closeSignup() {
    signupModal.close();
  }
  function showPasswordReset() {
    if (signinModal.isOpen) {
      closeSignin();
    }
    passwordResetModal.showModal();
  }
  function closePasswordReset() {
    passwordResetModal.close();
  }
  /*** @param {KeyboardEvent} event */
  function handleSignInSignUpEscape(event) {
    if (event.code === "Escape") {
      toggleSignInSignupButtons();
    }
  }
  function handleClickOnBody() {
    toggleSignInSignupButtons();
  }
  /** @param {Event} event*/
  function stopEventPropagation(event) {
    event.stopPropagation();
  }

  // initial signin button menu
  function toggleSignInSignupButtons() {
    if (openedSignInSignupButtons) {
      document.removeEventListener("keyup", handleSignInSignUpEscape);
      document.removeEventListener("click", handleClickOnBody);
      headerCustomerModuleElement.removeEventListener("click", stopEventPropagation);
    } else {
      document.addEventListener("keyup", handleSignInSignUpEscape);
      document.addEventListener("click", handleClickOnBody, { once: true });
      headerCustomerModuleElement.addEventListener("click", stopEventPropagation);
    }
    openedSignInSignupButtons = !openedSignInSignupButtons;
  }

  // resets the sign in and sign up error messages
  function clearErrorMessage() {
    errorMessage = null;
  }
  /**
   * Generic menu action to allow for side effects to take place when a
   * customer menu option is selected.
   *
   * @param {() => void} callBack*/
  function signInSignUpMenuAction(callBack) {
    callBack();
    toggleSignInSignupButtons();
  }

  /**@param {{ currentTarget: HTMLFormElement}} e*/
  async function submit(e) {
    clearErrorMessage();
    const action = e.currentTarget?.action;
    const name = e.currentTarget?.name;
    const formData = new FormData(e.currentTarget);
    const recaptcha = await getRecaptcha();

    try {
      // shortcut for customer signup
      if (name == "signin") {
        const customerSigninResponse = await fetchCustomerSignin(
          /**@type {string}*/ (action),
          formData,
          recaptcha
        );

        if (customerSigninResponse) {
          e.currentTarget?.reset();
          setAllItemsInToCart();
          dispatchCustomEvent(LOGIN, formData.get("username"));
          if (signinModal?.isOpen) {
            closeSignin();
          }

          if (signupModal?.isOpen) {
            closeSignup();
          }

          if (passwordResetModal?.isOpen) {
            closePasswordReset();
          }
        }
        return customerSigninResponse;
      }

      // all other fetches
      const res = await fetch(/** @type {string} */ (action), {
        method: "POST",
        headers: {
          "X-ReCaptcha": recaptcha,
        },
        body: formData,
      });

      const response = await res.json();

      if (res.status == 200) {
        e.currentTarget?.reset();
        setAllItemsInToCart();
        dispatchCustomEvent(SIGN_UP, formData.get("email"));
        if (signinModal?.isOpen) {
          closeSignin();
        }
        if (signupModal?.isOpen) {
          closeSignup();
        }
        if (passwordResetModal?.isOpen) {
          closePasswordReset();
        }
        return;
      }
      throw new Error(response.message);
    } catch (error) {
      console.error(error);
      errorMessage = /** @type {Error} */ (error).message;
    }
  }

  afterUpdate(() => {
    document.addEventListener("open-signin", () => toggleSignInSignupButtons());
    return () => document.removeEventListener("open-signin", () => toggleSignInSignupButtons());
  });

  const validatePasswordTest = () => (passwordError = validatePassword(password, passwordConfirm));
  const resetValidate = () => (passwordError = null);
</script>

<!-- Action buttons -->
<div class="wrapper" bind:this={headerCustomerModuleElement}>
  <!-- Icon user for open modals -->
  <button
    on:click={toggleSignInSignupButtons}
    class="avatar"
    aria-labelledby="customer-signedin-icon"
  >
    <CustomerButtonAvatar />
  </button>
  <!-- If User sign in app -->
  {#if openedSignInSignupButtons}
    <div class="signin-or-signup">
      {#if account}
        <!-- Account button -->
        <NaButton
          callbackAction={() => goto("/customers/account")}
          text="My Account"
          type="button"
          --color="#fdfdfd"
          --bgColor="#1e2225"
        />
        <p class="not-customer">Not {$customer?.customer.firstname}?</p>
        <!-- Sign out button -->
        <NaButton
          callbackAction={() => goto("/customers/signout")}
          text="Sign Out"
          type="button"
          --color="#fdfdfd"
          --bgColor="#1e2225"
        />
        <!-- If User not sign in app -->
      {:else}
        <!-- Signin button -->
        <NaButton
          callbackAction={() => signInSignUpMenuAction(showSignin)}
          text="Sign in"
          type="button"
          --color="#fdfdfd"
          --bgColor="#1e2225"
        />
        <!-- Signup button -->
        <NaButton
          callbackAction={() => goto("/signup")}
          text="Create an account"
          --color="#fdfdfd"
          --bgColor="#1e2225"
        />
        <!-- Forgot password -->
        <a
          class="password-forgotten"
          type="button"
          href="/forget-password"
          aria-labelledby="forget-password-button"
        >
          Forgot Your Password?
        </a>
      {/if}
    </div>
  {/if}
</div>
<!-- End action buttons -->

<NaModal bind:this={passwordResetModal} on:close={clearErrorMessage}>
  <form
    action="/customers/password-forgotten"
    method="post"
    name="password"
    on:submit|preventDefault={submit}
    style="padding-bottom: 60px;"
  >
    <h1>Forgot your password?</h1>
    <p>Please enter the email address and we'll send you a link to reset your password.</p>
    <input type="email" name="email" placeholder="E-Mail" autocomplete="email" />
    <div class="buttons-row">
      <NaButton
        text="Submit"
        callbackAction={() => SubmitEvent}
        type="submit"
        --color="#fdfdfd"
        --bgColor="#1e2225"
      />
    </div>
  </form>
</NaModal>

<NaModal bind:this={signinModal} on:close={clearErrorMessage}>
  <form action="/customers/signin"   style="padding-bottom: 30px;" method="post" name="signin" on:submit|preventDefault={submit}>
    <h1>Sign In</h1>
    <p>Enjoy exclusive deals, offers, invites and&nbsp;rewards.</p>
    <div role="alert">
      <p class="error" hidden={errorMessage == null}>{errorMessage}</p>
    </div>
    <input
      type="email"
      name="username"
      autocomplete="email"
      placeholder="E-Mail"
      on:change={() => (errorMessage = null)}
    />
    <input type="password" name="password" placeholder="Password" />
    <button class="password-forgotten" type="button" on:click={showPasswordReset}
      >Forgot Your Password?</button
    >
    <div class="buttons-row">
      <NaButton
        text="Sign in"
        callbackAction={() => SubmitEvent}
        type="submit"
        --color="#fdfdfd"
        --bgColor="#1e2225"
      />
      <NaButton
        callbackAction={showSignup}
        text="Create an account"
        type="button"
        --color="#fdfdfd"
        --bgColor="#1e2225"
      />
    </div>
  </form>
</NaModal>

<NaModal bind:this={signupModal} on:close={clearErrorMessage}>
  <form
    style="padding-bottom: 60px;"
    action="/customers/signup"
    method="post"
    name="signup"
    on:submit|preventDefault={submit}
  >
    <h1>Create an account</h1>
    <p>Enjoy exclusive deals, offers, invites and&nbsp;rewards.</p>
    <div role="alert">
      <p class="error" hidden={errorMessage == null}>{errorMessage}</p>
    </div>
    <section>
      <h2>Personal Info</h2>
      <input
        type="text"
        name="firstname"
        autocomplete="given-name"
        placeholder="First Name"
        required
      />
      <input
        type="text"
        name="lastname"
        autocomplete="family-name"
        placeholder="Last Name"
        required
      />
      <input type="checkbox" name="is_subscribed" />
      <label for="newsletter">Signup for newsletter</label>
      <input type="checkbox" name="assistance_allowed" />
      <label for="remote-shopping-assistance"> Allow remote shopping assistance </label>
    </section>
    <section>
      <h2>Sign In</h2>
      <input type="email" name="email" autocomplete="email" placeholder="E-Mail" required />
      {#if !showPassword}
        <ErrorMessage errorMessage={passwordError} />
        <input
          type="password"
          name="password"
          autocomplete="new-password"
          placeholder="Password"
          bind:value={password}
          minlength="8"
          maxlength="20"
          required
        />
        <input
          type="password"
          name="password-confirm"
          autocomplete="off"
          placeholder="Confirm Password"
          bind:value={passwordConfirm}
          on:blur={validatePasswordTest}
          on:keyup={resetValidate}
        />
      {:else}
        <input
          type="text"
          name="password"
          autocomplete="new-password"
          placeholder="Password"
          bind:value={password}
          required
        />
      {/if}
      <input type="checkbox" name="showpassword" bind:checked={showPassword} />
      <label for="showpassword">Show password</label>
    </section>
    <section>
      <h2>Licensing Information</h2>
      <select name="occupation" title="Occupation" required>
        <option value="Non-Professional">General Consumer</option>
        <option value="Nail Technician">Nail Technician</option>
      </select>
      <input type="text" name="license_number" placeholder="License Number" autocomplete="off" />
      <select name="state" title="State" class="input-select">
        {#if states.length > 0}
          {#each states as state}
            <option value={state}>{state}</option>
          {/each}
        {/if}
      </select>
    </section>
    <section class="buttons-row">
      <NaButton
        text="Create account"
        type="submit"
        callbackAction={() => SubmitEvent}
        --color="#fdfdfd"
        --bgColor="#1e2225"
      />
    </section>
  </form>
</NaModal>

<style>
  a {
    text-decoration: none;
  }
  .wrapper {
    position: relative;
  }
  button {
    border: none;
    background: transparent;
    cursor: pointer;
    width: 24px;
  }

  .signin-or-signup {
    position: absolute;
    background: white;
    padding: 30px 38px 23px;
    display: flex;
    flex-direction: column;
    align-items: center;
    min-width: 254px;
    gap: 16px;
    right: 0;
    top: calc(var(--header-height) - 15px);
    box-shadow: 0px 3px 6px rgba(0, 0, 0, 0.75);
    z-index: 60;
    border-radius: 20px 0 20px 20px;
  }
  .signin-or-signup .password-forgotten {
    color: #8c8c8c;
    align-self: initial;
    font-size: 0.75em;
  }
  form {
    display: flex;
    flex-direction: column;
    align-items: center;
  }
  h1 {
    margin-top: 0;
    text-align: center;
    font-size: 2.125em;
    font-weight: 500;
    letter-spacing: 0.05em;
  }
  p {
    text-align: center;
    font-size: 0.875em;
    max-width: 80%;
    line-height: 1rem;
    letter-spacing: 0.034em;
    margin-bottom: 2.8125em;
  }
  button.password-forgotten {
    text-align: left;
    align-self: flex-start;
    font-size: 0.875em;
    line-height: 1rem;
    letter-spacing: 0.034em;
    width: max-content;
    aspect-ratio: inherit;
  }
  .buttons-row {
    display: flex;
    flex-direction: column;
    gap: 16px;
    align-items: center;
    margin-top: 62px;
  }
  :global(dialog .buttons-row button) {
    width: 200px !important;
  }
  .not-customer {
    text-transform: capitalize;
    margin: 0;
  }

  button.avatar {
    background: transparent;
    border: 0;
    padding: 5px 0 0 0;
    margin: 0;
    width: 24px;
  }

  /* signup */

  h2 {
    font-size: 1.5em;
    border-bottom: 3px solid #000;
    width: fit-content;
    margin: 33px auto;
  }

  label {
    color: #707070;
  }

  label::after {
    content: "";
    display: block;
  }

  /* ocupation */

  select {
    border: 0;
    background: white;
    border-bottom: 1px solid #000;
    width: 100%;
    padding: 3px;
  }

  @media (min-width: 768px) {
    button.avatar {
      width: 28px;
    }
  }
</style>
